import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Avatar,
  Chip,
  Alert,
} from "@mui/material";
import {
  Person,
  Save,
  Edit,
  Logout,
  VerifiedUser,
  ErrorOutline,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { updateUserProfile, logout } from "../../redux/slices/userSlice";
import { useNavigate } from "react-router-dom";

const ProfileInfo = ({ user, loading, error }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({ defaultValues: user });

  useEffect(() => {
    if (user) reset(user);
  }, [user, reset]);

  const handleLogout = () => {
    dispatch(logout());
    setTimeout(() => navigate("/auth"), 100);
  };

  const onSubmit = async (data) => {
    try {
      await dispatch(updateUserProfile(data)).unwrap();
      setSuccessMessage("Profile updated successfully!");
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setSuccessMessage("");
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <Grid size={{ xs: 12 }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          bgcolor: "background.paper",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Person sx={{ color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Profile Information
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error.message || error}
          </Alert>
        )}

        {/* Avatar Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3 }}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: "primary.main",
              fontSize: "1.75rem",
            }}
          >
            {user?.firstName?.[0]}
            {user?.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
              <Chip
                label={user?.status}
                color={user?.status === "active" ? "success" : "default"}
                size="small"
              />
              <Chip
                icon={user?.emailVerified ? <VerifiedUser /> : <ErrorOutline />}
                label={
                  user?.emailVerified ? "Email Verified" : "Email Not Verified"
                }
                color={user?.emailVerified ? "success" : "warning"}
                size="small"
                variant={user?.emailVerified ? "filled" : "outlined"}
              />
            </Box>
          </Box>
        </Box>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                size="small"
                disabled={!isEditing}
                {...register("firstName", {
                  required: "First name is required",
                  minLength: { value: 2, message: "Min 2 characters" },
                })}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                size="small"
                disabled={!isEditing}
                {...register("lastName", {
                  required: "Last name is required",
                  minLength: { value: 2, message: "Min 2 characters" },
                })}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                size="small"
                disabled={!isEditing}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email address",
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Member Since
              </Typography>
              <Typography variant="body2">
                {user?.createdAt
                  ? new Date(user.createdAt).toLocaleDateString()
                  : "—"}
              </Typography>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              color="error"
              startIcon={<Logout />}
              onClick={handleLogout}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Logout
            </Button>
            {isEditing ? (
              <Button
                variant="contained"
                startIcon={<Save />}
                type="submit"
                disabled={!isDirty}
                disableElevation
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Save Changes
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
                disableElevation
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Edit Profile
              </Button>
            )}
          </Box>
        </form>
      </Box>
    </Grid>
  );
};

export default ProfileInfo;
